import { useEffect, useMemo, useRef, useState } from "react";
import {
  messageAdminService,
  AdminConversation,
  AdminMessage,
  AdminMessageAttachment,
} from "../../services/message-admin.service";

function formatTime(t: number) {
  return new Date(t).toLocaleTimeString("es-MX", { hour: "2-digit", minute: "2-digit" });
}

function formatRelative(t: number | null) {
  if (!t) return "";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  const h = Math.floor(diff / 3600000);
  const d = Math.floor(diff / 86400000);
  if (min < 1) return "Ahora";
  if (min < 60) return `${min} min`;
  if (h < 24) return `${h} h`;
  if (d < 7) return `${d} d`;
  return new Date(t).toLocaleDateString("es-MX", { day: "2-digit", month: "short" });
}

function formatDate(t: number) {
  const d = new Date(t);
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return "Hoy";
  if (d.toDateString() === yesterday.toDateString()) return "Ayer";
  return d.toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
}

export function MessagesPage() {
  const [conversations, setConversations] = useState<AdminConversation[]>([]);
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [messages, setMessages] = useState<AdminMessage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMessages, setIsLoadingMessages] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [pendingAttachments, setPendingAttachments] = useState<AdminMessageAttachment[]>([]);
  const [isSending, setIsSending] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadConversations = async () => {
    try {
      const data = await messageAdminService.listConversations();
      setConversations(data);
      if (!selectedProjectId && data.length > 0) {
        setSelectedProjectId(data[0].project_id);
      }
    } catch (err: any) {
      setError(err?.message || "Error al cargar conversaciones");
    } finally {
      setIsLoading(false);
    }
  };

  const loadMessages = async (projectId: string, markRead = true) => {
    setIsLoadingMessages(true);
    try {
      const data = await messageAdminService.listMessages(projectId);
      setMessages(data.messages);
      if (markRead && data.messages.some((m) => m.isFromClient && !m.readAt)) {
        await messageAdminService.markRead(projectId);
        // Refrescar unread en la lista
        loadConversations();
      }
    } catch (err: any) {
      setError(err?.message || "Error al cargar mensajes");
    } finally {
      setIsLoadingMessages(false);
    }
  };

  useEffect(() => {
    loadConversations();
    const interval = setInterval(loadConversations, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProjectId) {
      loadMessages(selectedProjectId, true);
      const interval = setInterval(() => loadMessages(selectedProjectId, false), 15000);
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProjectId]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const grouped = useMemo(() => {
    const groups: { date: string; messages: AdminMessage[] }[] = [];
    let current = "";
    for (const m of messages) {
      const d = formatDate(m.createdAt);
      if (d !== current) {
        current = d;
        groups.push({ date: d, messages: [] });
      }
      groups[groups.length - 1].messages.push(m);
    }
    return groups;
  }, [messages]);

  const selectedConv = conversations.find((c) => c.project_id === selectedProjectId);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setIsUploading(true);
    setError(null);
    try {
      const uploaded: AdminMessageAttachment[] = [];
      for (const file of Array.from(files)) {
        if (file.size > 10 * 1024 * 1024) throw new Error(`${file.name} excede 10MB`);
        const presigned = await messageAdminService.presignUpload({
          filename: file.name,
          contentType: file.type || "application/octet-stream",
        });
        const res = await fetch(presigned.upload_url, {
          method: "PUT",
          headers: { "Content-Type": file.type || "application/octet-stream" },
          body: file,
        });
        if (!res.ok) throw new Error(`Error al subir ${file.name}`);
        uploaded.push({
          url: presigned.public_url,
          name: file.name,
          type: file.type,
          size: file.size,
        });
      }
      setPendingAttachments((prev) => [...prev, ...uploaded]);
    } catch (err: any) {
      setError(err?.message || "Error al subir");
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSend = async () => {
    if (!selectedProjectId) return;
    if (!newMessage.trim() && pendingAttachments.length === 0) return;
    setIsSending(true);
    setError(null);
    try {
      const sent = await messageAdminService.sendMessage({
        projectId: selectedProjectId,
        content: newMessage.trim() || "(adjunto)",
        attachments: pendingAttachments.length > 0 ? pendingAttachments : undefined,
      });
      setMessages((prev) => [...prev, sent]);
      setNewMessage("");
      setPendingAttachments([]);
    } catch (err: any) {
      setError(err?.message || "Error al enviar");
    } finally {
      setIsSending(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const isImage = (t: string) => t.startsWith("image/");
  const isPdf = (t: string) => t === "application/pdf";

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex h-[calc(100vh-120px)] -mx-4 lg:-mx-6">
      {/* Sidebar conversaciones */}
      <div className="w-80 border-r border-slate-200 bg-white flex flex-col">
        <div className="p-4 border-b border-slate-200">
          <h2 className="font-bold text-slate-900">Conversaciones</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {conversations.length} proyecto{conversations.length !== 1 ? "s" : ""}
          </p>
        </div>
        <div className="flex-1 overflow-y-auto">
          {conversations.length === 0 ? (
            <div className="p-6 text-center text-sm text-slate-500">
              No lideras proyectos aún.
            </div>
          ) : (
            conversations.map((conv) => (
              <button
                key={conv.project_id}
                onClick={() => setSelectedProjectId(conv.project_id)}
                className={`w-full text-left p-3 border-b border-slate-100 transition ${
                  selectedProjectId === conv.project_id
                    ? "bg-primary-light"
                    : "hover:bg-slate-50"
                }`}
              >
                <div className="flex items-start justify-between gap-2 mb-1">
                  <p className="font-semibold text-sm text-slate-900 truncate">
                    {conv.client_name}
                  </p>
                  <span className="text-xs text-slate-400 shrink-0">
                    {formatRelative(conv.last_message_at)}
                  </span>
                </div>
                <p className="text-xs text-primary truncate mb-0.5">
                  {conv.project_name}
                </p>
                <div className="flex items-center gap-2">
                  <p className="text-xs text-slate-500 truncate flex-1">
                    {conv.last_message_preview || "Sin mensajes"}
                  </p>
                  {conv.unread_count > 0 && (
                    <span className="bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center font-bold shrink-0">
                      {conv.unread_count > 9 ? "9+" : conv.unread_count}
                    </span>
                  )}
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat */}
      <div className="flex-1 flex flex-col bg-slate-50 min-w-0">
        {!selectedConv ? (
          <div className="flex-1 flex items-center justify-center text-slate-400">
            <div className="text-center">
              <div className="text-5xl mb-2 opacity-40">💬</div>
              <p className="text-sm">Selecciona una conversación</p>
            </div>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="bg-white border-b border-slate-200 px-4 py-3">
              <p className="font-semibold text-slate-900">{selectedConv.client_name}</p>
              <p className="text-xs text-slate-500">{selectedConv.project_name}</p>
            </div>

            {/* Mensajes */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-6">
              {isLoadingMessages && messages.length === 0 ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
                </div>
              ) : messages.length === 0 ? (
                <div className="text-center py-12 text-slate-400 text-sm">
                  No hay mensajes aún.
                </div>
              ) : (
                grouped.map((g) => (
                  <div key={g.date}>
                    <div className="text-center mb-3">
                      <span className="bg-slate-200 text-slate-600 text-xs px-3 py-1 rounded-full">
                        {g.date}
                      </span>
                    </div>
                    <div className="space-y-3">
                      {g.messages.map((msg) => {
                        const isOwn = !msg.isFromClient;
                        return (
                          <div
                            key={msg.id}
                            className={`flex ${isOwn ? "justify-end" : "justify-start"}`}
                          >
                            <div
                              className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                                isOwn
                                  ? "bg-primary text-white"
                                  : "bg-white border border-slate-200"
                              }`}
                            >
                              {!isOwn && (
                                <p className="text-xs font-semibold mb-1 opacity-70">
                                  {msg.senderName}
                                </p>
                              )}
                              {msg.content && msg.content !== "(adjunto)" && (
                                <p className="text-sm whitespace-pre-wrap break-words">
                                  {msg.content}
                                </p>
                              )}
                              {msg.attachments.length > 0 && (
                                <div className="mt-2 space-y-2">
                                  {msg.attachments.map((att, i) => (
                                    <a
                                      key={i}
                                      href={att.url}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="block"
                                    >
                                      {isImage(att.type) ? (
                                        <img
                                          src={att.url}
                                          alt={att.name}
                                          className="rounded-lg max-w-[240px] max-h-[240px] object-cover"
                                        />
                                      ) : (
                                        <div
                                          className={`flex items-center gap-2 p-2 rounded-lg ${
                                            isOwn ? "bg-white/10" : "bg-slate-100"
                                          }`}
                                        >
                                          <span>{isPdf(att.type) ? "📄" : "📎"}</span>
                                          <span className="text-xs truncate flex-1">
                                            {att.name}
                                          </span>
                                        </div>
                                      )}
                                    </a>
                                  ))}
                                </div>
                              )}
                              <div
                                className={`text-[10px] mt-1 text-right ${
                                  isOwn ? "text-white/70" : "text-slate-400"
                                }`}
                              >
                                {formatTime(msg.createdAt)}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Composer */}
            <div className="bg-white border-t border-slate-200 px-4 py-3">
              {pendingAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {pendingAttachments.map((att, i) => (
                    <div
                      key={i}
                      className="bg-slate-100 rounded-lg px-2 py-1 flex items-center gap-2 text-xs"
                    >
                      <span>{isImage(att.type) ? "🖼️" : isPdf(att.type) ? "📄" : "📎"}</span>
                      <span className="truncate max-w-[120px]">{att.name}</span>
                      <button
                        onClick={() =>
                          setPendingAttachments((prev) => prev.filter((_, j) => j !== i))
                        }
                        className="text-red-500"
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded-lg p-2 mb-2">
                  {error}
                </div>
              )}
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isUploading || isSending}
                  className="p-2.5 rounded-lg text-slate-500 hover:bg-slate-100 transition disabled:opacity-50"
                >
                  {isUploading ? "⏳" : "📎"}
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  accept="image/*,application/pdf,.doc,.docx"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Responder al cliente..."
                  rows={1}
                  maxLength={2000}
                  disabled={isSending}
                  className="flex-1 resize-none px-3 py-2.5 border border-slate-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/15 outline-none text-sm max-h-32"
                  style={{ minHeight: "42px" }}
                />
                <button
                  onClick={handleSend}
                  disabled={isSending || (!newMessage.trim() && pendingAttachments.length === 0)}
                  className="px-4 py-2.5 bg-primary hover:bg-primary-dark text-white rounded-lg font-semibold transition disabled:opacity-50 shrink-0"
                >
                  {isSending ? "⏳" : "Enviar"}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}